import { ApiError, OfflineError, type ApiClient, type ChangeRequest, type ServerDoc } from './api'
import {
  CURSOR_KEY,
  deleteBlob,
  dirtyDocs,
  failedBlobs,
  finishPhotoUpload,
  getBlob,
  getDoc,
  getMeta,
  LAST_SYNC_KEY,
  pendingBlobs,
  putBlob,
  putDocs,
  setMeta,
  type LocalBlob,
  type LocalDoc,
} from './db'
import { mergeRemote } from './merge'
import { KIND } from '../lib/types'

/**
 * Zustand der Synchronisation, wie ihn die Statusleiste anzeigt.
 *
 * 'kein-zugriff' ist seit dem Reisemodell dazugekommen: die Reise ist weg oder
 * die Rolle reicht nicht mehr. Das ist ausdrücklich kein Datenverlust und
 * deshalb auch kein 'error' - der lokale Bestand bleibt stehen, und der
 * Aufrufer führt zurück zur Reiseliste.
 */
export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'signed-out' | 'kein-zugriff'

/**
 * Warum eine Reise nicht mehr erreichbar ist.
 *
 * - 'weg': 404. Entweder wurde die Reise gelöscht, oder dem Aufrufer wurde die
 *   Mitgliedschaft entzogen. Der Server unterscheidet das mit Absicht nicht -
 *   Mitgliedschaft ist Sichtbarkeit -, und der Client kann es folglich auch
 *   nicht. Beides ist von hier aus dasselbe: es gibt sie für dich nicht mehr.
 * - 'verboten': 403. Die Reise gibt es, aber die Rolle reicht für diesen
 *   Zugriff nicht mehr; jemand hat sie herabgestuft.
 */
export type ZugriffWeg = 'weg' | 'verboten'

export interface SyncStatus {
  state: SyncState
  /** Zeitpunkt des letzten erfolgreichen Abgleichs. */
  lastSync: number | null
  /** Wie viele Änderungen noch auf dem Gerät warten. */
  pending: number
  /** Fotos, die der Server dauerhaft ablehnt. Die bleiben liegen. */
  failedUploads: number
  message?: string
  /**
   * Was der Server an Änderungen abgewiesen hat, in einem Satz.
   *
   * Kein Fehler und kein Zustand: der Abgleich ist geglückt, nur eine
   * einzelne Änderung war nicht erlaubt - der Versuch, den Beitrag eines
   * anderen zu ändern. Still darüber hinwegzugehen wäre falsch, denn der
   * eigene Text ist dabei verschwunden; eine Fehlermeldung wäre es auch, denn
   * kaputt ist nichts.
   */
  abweisung?: string
}

/**
 * Was der Server an Änderungen abgewiesen hat, nach Grund getrennt.
 *
 * Zwei Zahlen und nicht eine: der Server weist aus zwei verschiedenen Gründen
 * ab, und ein Satz, der beide über einen Kamm schert, erklärt keinen von
 * beiden.
 */
export interface Abweisungen {
  /** Beiträge, deren Verfasser jemand anderes ist. */
  beitraege: number
  /** Tagebucheinträge, deren alter Text sich nicht mehr setzen lässt. */
  eintraege: number
}

/** Keine Abweisung. Ausgangswert und das, was ein Durchgang ohne Push meldet. */
export const KEINE_ABWEISUNG: Abweisungen = { beitraege: 0, eintraege: 0 }

/** Zwei Zählungen zusammen - ein Durchgang kann zweimal pushen. */
export function zaehleAbweisungen(a: Abweisungen, b: Abweisungen): Abweisungen {
  return { beitraege: a.beitraege + b.beitraege, eintraege: a.eintraege + b.eintraege }
}

/**
 * Der Satz zu abgewiesenen Änderungen, oder nichts.
 *
 * Reine Funktion, damit die Formulierung prüfbar ist und nicht mitten im
 * Ablauf steht. Sie erklärt, was passiert ist und was jetzt dasteht - der
 * Nutzer hat meist gar nicht bemerkt, dass er an einem fremden Beitrag war
 * (die Ansicht war kurz veraltet, oder er hat sein Gerät an jemanden
 * weitergereicht).
 */
export function abweisungHinweis({ beitraege, eintraege }: Abweisungen): string | undefined {
  const teile: string[] = []
  if (beitraege > 0) {
    teile.push(
      beitraege === 1
        ? 'Ein Tagebuchbeitrag gehört jemand anderem: ändern darf ihn nur die Person, die ihn geschrieben hat'
        : `${String(beitraege)} Tagebuchbeiträge gehören jemand anderem: ändern darf sie nur die Person, die sie geschrieben hat`,
    )
  }
  if (eintraege > 0) {
    teile.push(
      eintraege === 1
        ? 'Der alte Tagebuchtext einer Etappe lässt sich nur noch überführen, nicht mehr ändern'
        : `Der alte Tagebuchtext von ${String(eintraege)} Etappen lässt sich nur noch überführen, nicht mehr ändern`,
    )
  }
  if (teile.length === 0) return undefined
  return `${teile.join('. ')}. Deine Fassung wurde verworfen, es steht wieder die vom Server.`
}

/**
 * Der Abgleichzustand, den das Stilllegen für das Abmelden hinterlässt.
 *
 * Ab dem Stilllegen nimmt die Datenschicht nichts mehr an: die Datenbank ist
 * geleert, der Abgleich steht, und jeder weitere Schreibvorgang wird verworfen
 * (siehe stillgelegtRef in RoadbookProvider). Normalerweise fällt das nicht
 * auf, weil der Provider gleich darauf abgebaut wird - aber wenn die Abmeldung
 * beim Anmeldedienst scheitert, und zwar in beiden Anläufen, bleibt der
 * Anmeldezustand auf ready stehen und die App steht weiter da. Ohne diesen
 * Zustand sähe sie benutzbar aus und speicherte nichts.
 *
 * Der Weg zurück ist ausdrücklich nicht die Lösung: die Stilllegung wieder
 * aufzuheben hiesse, mit einem weiterhin gültigen Token abzugleichen - und der
 * nächste Pull holte den gesamten Bestand des eben abgemeldeten Nutzers auf
 * dieses Gerät zurück. Genau das soll das Löschen verhindern. Also bleibt es
 * beim Verweigern, aber sichtbar: die Statusleiste sagt "Abgemeldet" und
 * verweist auf die neue Anmeldung, die den Provider ohnehin neu aufbaut.
 *
 * Zählungen und Zeitpunkt fallen dabei weg. Sie beschreiben einen Bestand, den
 * es nicht mehr gibt - wipe() nimmt auch den gemerkten Zeitpunkt mit.
 */
export function statusNachStilllegung(status: SyncStatus): SyncStatus {
  return {
    ...status,
    state: 'signed-out',
    lastSync: null,
    pending: 0,
    failedUploads: 0,
    message: 'Der lokale Bestand wurde gelöscht. Zum Weiterarbeiten neu anmelden.',
    abweisung: undefined,
  }
}

/**
 * Entscheidet, ob ein Fehler beim Hochladen eines Fotos dauerhaft ist.
 *
 * Dauerhaft heisst: ein weiterer Versuch mit denselben Bytes bringt dasselbe
 * Ergebnis, das Bild muss aus der Warteschlange. Vorübergehend heisst: der
 * nächste Durchlauf soll es erneut versuchen, deshalb bricht der Aufrufer die
 * Runde ab, statt das Bild zu verwerfen.
 *
 * 401 und 403 gelten hier bewusst nicht als dauerhaft: sie betreffen nicht das
 * Bild, sondern die Anmeldung, und müssen nach oben durchschlagen.
 */
export function isPermanentUploadFailure(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  const { status } = err
  // Alles ausserhalb der 4xx ist Serversache oder Netzsache: später nochmal.
  if (status < 400 || status >= 500) return false
  // 401/403 gehören dem Auth-Zustand, 408 und 429 sind ausdrückliche
  // Aufforderungen, es später erneut zu versuchen.
  return status !== 401 && status !== 403 && status !== 408 && status !== 429
}

/**
 * Wie oft der Server ein Bild mit einem Fehler beantworten darf, bevor es als
 * dauerhaft abgelehnt gilt.
 *
 * Die Einordnung nach Statuscode reicht nicht: antwortet der Server auf ein
 * bestimmtes Bild reproduzierbar mit 500, oder bricht ein Proxy grosse Uploads
 * ab, während kleine Aufrufe durchgehen, bliebe das Bild sonst für immer in
 * der Warteschlange.
 */
export const MAX_UPLOAD_ATTEMPTS = 5

/**
 * Vermerkt einen gescheiterten Upload am Bild.
 *
 * Reine Funktion: sie entscheidet nur, wie der Eintrag danach aussieht.
 * Gezählt werden ausschliesslich Versuche, die der Server beantwortet hat -
 * ohne Netz hat das Bild nichts falsch gemacht, und Anmeldeprobleme betreffen
 * die Sitzung, nicht die Bytes. In beiden Fällen kommt der Eintrag unverändert
 * zurück, der Aufrufer braucht dann gar nicht zu schreiben.
 */
export function noteUploadFailure(item: LocalBlob, err: unknown): LocalBlob {
  const reason = err instanceof Error ? err.message : 'Unbekannter Fehler'
  const attempts = (item.attempts ?? 0) + 1

  // Zu gross, falsches Format, unverständlich: ein weiterer Versuch ändert
  // nichts.
  if (isPermanentUploadFailure(err)) {
    return { ...item, attempts, failed: 1, failedReason: reason }
  }
  // Anmeldung und Rolle betreffen die Sitzung, nicht die Bytes: das Bild hat
  // nichts falsch gemacht und behält seine Zählung. Eine Mengengrenze meint
  // dagegen genau dieses Bild und wird gezählt wie jede andere Absage.
  if (!(err instanceof ApiError) || err.isAuthProblem || istRollenAbsage(err)) return item

  // Vorübergehend, aber hartnäckig: irgendwann ist es dauerhaft.
  if (attempts >= MAX_UPLOAD_ATTEMPTS) {
    return {
      ...item,
      attempts,
      failed: 1,
      failedReason: `${reason} (nach ${attempts} Versuchen aufgegeben)`,
    }
  }
  return { ...item, attempts }
}

/**
 * Was mit den lokalen Bilddaten geschieht, wenn ein Grabstein für ein Foto
 * hereinkommt.
 *
 * 'apply' wendet den Grabstein an, 'apply-and-drop-bytes' zusätzlich mit den
 * Bytes, 'ignore' lässt beides in Ruhe.
 */
export type TombstoneAction = 'apply' | 'apply-and-drop-bytes' | 'ignore'

/**
 * Entscheidet über einen fremden Grabstein für ein Foto.
 *
 * Hochgeladene Bytes sind eine Kopie dessen, was der Server hatte: mit dem
 * Dokument können auch sie weg. Noch nicht hochgeladene Bytes sind dagegen
 * lokal entstandener Inhalt, den der Server nie gesehen hat - typisch nach dem
 * Import einer Sicherung, deren Foto inzwischen serverseitig gelöscht wurde.
 * Sie einem fremden Grabstein zu opfern, hiesse das Foto zu verlieren, bevor
 * der Upload es je senden konnte. Also wird der Grabstein übergangen und der
 * Upload stellt das Foto wieder her.
 *
 * Übergehen taugt aber nur so lange, wie es diesen Upload noch gibt. Ein Bild,
 * das der Server dauerhaft ablehnt oder das an der Versuchsgrenze gescheitert
 * ist, fällt aus der Warteschlange und kommt nie mehr los. Dann trägt das
 * Übergehen nichts mehr, es zementiert nur eine Abweichung: hier stände das
 * Foto für immer, überall sonst gilt der Grabstein.
 */
export function decidePhotoTombstone(blob: LocalBlob | undefined): TombstoneAction {
  if (!blob) return 'apply'
  if (blob.uploaded === 1) return 'apply-and-drop-bytes'
  if (blob.failed === 1) return 'apply-and-drop-bytes'
  return 'ignore'
}

/**
 * Holt den übergangenen Grabstein nach, wenn ein Upload endgültig aufgegeben
 * wird.
 *
 * Ein übergangener Grabstein kommt nie wieder: der Cursor läuft trotzdem
 * weiter, und einen Weg, ein einzelnes Dokument erneut anzufordern, gibt es
 * nicht. Solange der Upload noch aussteht, ist das gewollt - er stellt das Foto
 * beim Server wieder her. Fällt das Bild aber aus der Warteschlange, weil der
 * Server es dauerhaft ablehnt oder die Versuchsgrenze erreicht ist, wird aus
 * dem Aufschub eine dauerhafte Abweichung: hier steht das Foto weiter, überall
 * sonst ist es gelöscht. Dann zählt der Stand des Servers.
 *
 * Reine Funktion: das Schreiben passiert im Aufrufer.
 *
 * @returns der Grabstein, der lokal geschrieben werden muss, oder null, wenn es
 *   nichts nachzuholen gibt.
 */
export function tombstoneAfterGiveUp(
  blob: LocalBlob,
  current: LocalDoc | undefined,
  now: number,
): LocalDoc | null {
  if (blob.remoteDeletedRev === undefined) return null
  if (current?.deleted) return null
  return {
    kind: KIND.photo,
    id: blob.id,
    body: {},
    rev: blob.remoteDeletedRev,
    deleted: true,
    // Nicht schmutzig: der Server hat das Löschen längst, hier wird nur
    // nachgezogen. Ein Push brachte nur einen Konflikt mit sich selbst.
    dirty: 0,
    updatedAt: now,
    localSeq: current?.localSeq,
  }
}

/**
 * Ob dieses 403 die Rolle meint - und nicht eine Mengengrenze.
 *
 * Der Server antwortet auch auf zu viele Reisen und zu viele Fotos mit 403,
 * aber mit einem eigenen Code (too_many_trips, too_many_photos). Wer das
 * gleichbehandelte, warfe einen Nutzer aus seiner Reise, weil sein
 * fünftausendunderstes Foto nicht mehr hineinpasst.
 */
export function istRollenAbsage(err: unknown): boolean {
  return err instanceof ApiError && err.istVerboten && err.code === 'forbidden'
}

/**
 * Die Meldung zu einer verlorenen Reise, in einem Satz statt einer Nummer.
 *
 * Sie sagt ausdrücklich, dass nichts gelöscht wurde. Wer beim Abgleich
 * erfährt, dass "seine" Reise weg ist, denkt zuerst an sein Tagebuch.
 */
export function zugriffMeldung(grund: ZugriffWeg): string {
  if (grund === 'verboten') {
    return 'Die Rolle in dieser Reise reicht nicht mehr für den Abgleich. Der Bestand auf diesem Gerät bleibt erhalten.'
  }
  return 'Diese Reise gibt es für dich nicht mehr — sie wurde gelöscht, oder die Mitgliedschaft wurde entzogen. Der Bestand auf diesem Gerät bleibt erhalten.'
}

/** Wie oft im Hintergrund abgeglichen wird, solange die App sichtbar ist. */
const INTERVAL_MS = 60_000

/** Höchstzahl Änderungen je Push. Muss zum Serverlimit passen. */
const PUSH_BATCH = 100

type Listener = (status: SyncStatus) => void

/**
 * Hält den lokalen Bestand und den Server in Deckung.
 *
 * Der Ablauf ist immer derselbe: erst die eigenen Änderungen hochschieben,
 * dann die fremden holen. Fällt irgendwo das Netz aus, bleibt alles liegen und
 * der nächste Durchlauf holt es nach. Nichts geht dabei verloren, weil eine
 * Änderung erst dann als gesendet gilt, wenn der Server sie bestätigt hat.
 */
export interface SyncOptions {
  /** Wird gerufen, wenn sich der lokale Bestand geändert hat. */
  onDataChanged: () => void
  /**
   * Wird gerufen, wenn die Reise nicht mehr erreichbar ist.
   *
   * Der Aufrufer führt daraufhin zurück zur Reiseliste. Was er ausdrücklich
   * nicht tun darf, ist löschen: der lokale Bestand ist genau dann das
   * Einzige, was von der Reise noch da ist.
   */
  onZugriffWeg?: (grund: ZugriffWeg) => void
  /**
   * Ob dieses Gerät in dieser Reise schreiben darf (Rolle modify oder admin).
   *
   * Bei false läuft der Abgleich nur in eine Richtung: es wird geholt, aber
   * nichts gesendet. Der Server antwortete ohnehin mit 403, und ein Push, der
   * bei jedem Durchgang scheitert, macht aus einer Rolle einen Dauerfehler.
   */
  darfSchreiben?: boolean
}

export class SyncEngine {
  private status: SyncStatus = { state: 'idle', lastSync: null, pending: 0, failedUploads: 0 }
  private readonly listeners = new Set<Listener>()
  private timer: ReturnType<typeof setInterval> | null = null
  private running: Promise<void> | null = null
  private queuedAgain = false
  private stopped = true
  private paused = false
  /** Ob die Ereignisse des Fensters gerade an dieser Engine hängen. */
  private gebunden = false
  /** Zählt die Starts, damit ein überholter start() nichts mehr anrichtet. */
  private startCount = 0
  private readonly onDataChanged: () => void
  private readonly onZugriffWeg: (grund: ZugriffWeg) => void
  private readonly darfSchreiben: boolean

  /**
   * @param tripId die Reise, die dieser Abgleich bedient. Sie bestimmt beides:
   *   den Pfad beim Server und die Datenbank auf dem Gerät. Eine Engine
   *   gehört genau einer Reise; beim Wechsel wird sie abgebaut und eine neue
   *   gebaut, statt die Reise unter ihr auszutauschen.
   */
  constructor(
    private readonly api: ApiClient,
    private readonly tripId: string,
    options: SyncOptions,
  ) {
    this.onDataChanged = options.onDataChanged
    this.onZugriffWeg = options.onZugriffWeg ?? (() => undefined)
    this.darfSchreiben = options.darfSchreiben ?? true
  }

  /** Startet den Hintergrundabgleich. */
  async start(): Promise<void> {
    if (!this.stopped) return
    this.stopped = false
    const started = ++this.startCount

    const lastSync = (await getMeta<number>(this.tripId, LAST_SYNC_KEY)) ?? null

    // Während des Wartens auf die Datenbank kann stop() gelaufen sein: React
    // führt Effekte im StrictMode doppelt aus, und im Betrieb wechselt der
    // Anmeldezustand. Ohne diese Prüfung hängen Listener und Intervall an
    // einer bereits beendeten Engine und niemand räumt sie je wieder ab.
    // Der Zähler fängt zusätzlich stop() gefolgt von start() ab: dann laufen
    // zwei Starts, und ohne ihn liefen hinterher zwei Intervalle.
    if (this.stopped || started !== this.startCount) return

    this.update({ lastSync })

    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
    document.addEventListener('visibilitychange', this.handleVisibility)
    this.gebunden = true
    this.timer = setInterval(() => void this.sync(), INTERVAL_MS)

    if (navigator.onLine) void this.sync()
    else this.update({ state: 'offline' })
  }

  /** Beendet den Hintergrundabgleich. */
  stop(): void {
    if (this.stopped) return
    this.stopped = true
    // Eine offene Vormerkung gehört zur beendeten Sitzung und fällt mit ihr
    // weg. Anders als beim Stilllegen gibt es hier nichts nachzuholen: ein
    // späterer start() gleicht von sich aus ab, und nach dem Abmelden soll
    // gerade nichts mehr laufen.
    this.queuedAgain = false
    // Nur abmelden, was auch angemeldet wurde. Ein stop() ohne vorheriges
    // start() gibt es wirklich: die Engine legt sich selbst still, wenn die
    // Reise weg ist, und das kann vor dem ersten Start passieren.
    if (this.gebunden) {
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
      document.removeEventListener('visibilitychange', this.handleVisibility)
      this.gebunden = false
    }
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    listener(this.status)
    return () => this.listeners.delete(listener)
  }

  getStatus(): SyncStatus {
    return this.status
  }

  /**
   * Stösst einen Abgleich an. Läuft bereits einer, wird genau ein weiterer
   * vorgemerkt: mehr bringt nichts, weniger würde Änderungen liegen lassen,
   * die während des laufenden Durchgangs entstanden sind.
   */
  async sync(): Promise<void> {
    if (this.stopped || this.paused) return
    if (this.running) {
      this.queuedAgain = true
      return this.running
    }

    // runOnce fängt seine Fehler selbst ab, das gemerkte Promise wird also
    // nicht abgelehnt. Darauf verlassen sich pause() und jeder Aufrufer, der
    // ohne await synchronisiert.
    this.running = this.runOnce().finally(() => {
      this.running = null
      if (!this.queuedAgain) return
      // Stillgelegt: die Vormerkung bleibt stehen. sync() liefe hier ins Leere,
      // und der vorgemerkte Durchgang - er steht für Änderungen, die während
      // des laufenden entstanden sind - wäre verloren. resume() holt ihn nach.
      if (this.paused) return
      this.queuedAgain = false
      void this.sync()
    })
    return this.running
  }

  /**
   * Legt den Abgleich still und kehrt erst zurück, wenn kein Durchgang mehr
   * läuft.
   *
   * Gedacht für den Import: er leert die Datenbank und schreibt sie neu. Kommt
   * mittendrin die Antwort eines noch offenen Pushs zurück, findet sie ihre
   * Dokumente nicht mehr und schriebe den alten Bestand über den frisch
   * importierten - unschmutzig, also für immer.
   *
   * Der Aufrufer muss resume() in einem finally rufen, sonst steht der Abgleich
   * bis zum nächsten Neustart der App.
   */
  async pause(): Promise<void> {
    this.paused = true
    // Ein gescheiterter Durchgang darf hier nicht durchschlagen. Beim Aufrufer
    // steht das await vor dem try/finally: eine Ablehnung liesse resume() aus,
    // und der Abgleich stände bis zum nächsten Start der App.
    try {
      await this.running
    } catch (err) {
      console.error('Abgleich vor dem Stilllegen fehlgeschlagen', err)
    }
  }

  /**
   * Nimmt den Abgleich wieder auf und holt einen vorgemerkten Durchgang nach.
   *
   * Vorgemerkt heisst: während des letzten Durchgangs kam ein weiterer Anstoss,
   * und der stand für Änderungen, die es beim Start des laufenden noch nicht
   * gab. Fiel das Stilllegen dazwischen, blieben die bis zum nächsten Anstoss
   * liegen. Dass der Import danach ohnehin abgleicht, ist Zufall und keine
   * Zusage, auf die sich das hier stützen darf.
   */
  resume(): void {
    this.paused = false
    if (!this.queuedAgain) return
    this.queuedAgain = false
    void this.sync()
  }

  /**
   * Ein Durchgang, von dem nichts nach aussen dringt.
   *
   * Alles liegt im try, auch die Abfrage des Netzzustands und der Zählung: die
   * greift auf die Datenbank zu, und die kann werfen, wenn der Browser die
   * Verbindung kappt oder im privaten Modus gar keine hergibt. Träte das nach
   * aussen, läge das gemerkte Promise abgelehnt vor - pause() bliebe daran
   * hängen und liesse den Abgleich stillgelegt zurück, und jeder Aufruf ohne
   * await erzeugte eine unbehandelte Ablehnung.
   */
  private async runOnce(): Promise<void> {
    try {
      if (!navigator.onLine) {
        this.update({ state: 'offline', ...(await this.counts()) })
        return
      }
      this.update({ state: 'syncing' })

      const push = await this.pushDocs()
      let changed = push.changed
      let abgewiesen = push.abgewiesen
      let gesendet = push.gesendet

      // Der Upload meldet seinen Fehler zurück, statt ihn zu werfen: ein Bild,
      // das der Server hartnäckig ablehnt, darf den Pull nicht aufhalten.
      // Sonst holte das Gerät nie wieder fremde Änderungen und divergierte
      // still - genau das Symptom, das die Einordnung verhindern sollte.
      // Anmeldeprobleme schlagen weiterhin sofort nach oben durch.
      const upload = await this.uploadPhotos()
      changed = upload.changed || changed

      // Ein Foto, das während seines eigenen Uploads gelöscht wurde, hat
      // einen schmutzigen Grabstein hinterlassen - entstanden nach dem Push
      // dieses Durchgangs. Ohne diesen zweiten Push bliebe das Bild bis zum
      // nächsten Durchgang auf dem Server stehen, obwohl der Nutzer es hier
      // längst gelöscht hat und die Statusleiste eine wartende Änderung
      // meldet. Der Fall ist selten, aber das Nachschieben kostet nur dann
      // etwas, wenn er eingetreten ist.
      if (upload.deleted) {
        const nach = await this.pushDocs()
        changed = nach.changed || changed
        abgewiesen = zaehleAbweisungen(abgewiesen, nach.abgewiesen)
        gesendet = nach.gesendet || gesendet
      }

      changed = (await this.pullDocs()) || changed
      if (changed) this.onDataChanged()

      // Der Pull ist durch, und genau darauf antwortet "zuletzt abgeglichen":
      // der Stand des Servers ist hier angekommen. Ein hängendes Foto ändert
      // daran nichts - es hält nur die eigene Änderung auf, nicht die fremden.
      // Den Zeitpunkt deshalb erst nach dem Fehler zu setzen, hiesse dem Nutzer
      // neben der Fehlermeldung eine Angabe zu zeigen, die älter ist als der
      // tatsächliche Stand, und zwar so lange, wie das Foto klemmt.
      const now = Date.now()
      await setMeta(this.tripId, LAST_SYNC_KEY, now)
      this.update({ lastSync: now })

      // Erst jetzt, mit dem Pull im Rücken, wird der Upload-Fehler gemeldet.
      // Der Zeitpunkt oben steht schon im Statusobjekt und überlebt das:
      // reportFailure setzt nur Zustand, Zahlen und Meldung.
      if (upload.error) throw upload.error

      this.update({
        state: 'idle',
        ...(await this.counts()),
        message: undefined,
        // Der Hinweis auf abgewiesene Änderungen bleibt stehen, bis wieder
        // etwas gesendet wurde: erst dann ist er beantwortet. Ein Durchgang
        // ohne eigene Änderungen sagt nichts darüber aus und darf ihn
        // deshalb nicht wegwischen - sonst wäre er nach einer Minute weg,
        // ohne dass jemand ihn gelesen hat.
        ...(gesendet ? { abweisung: abweisungHinweis(abgewiesen) } : {}),
      })
    } catch (err) {
      await this.reportFailure(err)
    }
  }

  private async reportFailure(err: unknown): Promise<void> {
    // Die Zählung liest die Datenbank, und wenn die der Grund des Fehlers war,
    // scheitert auch sie. Dann bleiben die zuletzt bekannten Zahlen stehen: eine
    // ungenaue Anzeige ist besser als ein Fehler im Fehlerpfad.
    const counts = await this.counts().catch(() => ({
      pending: this.status.pending,
      failedUploads: this.status.failedUploads,
    }))
    if (err instanceof OfflineError) {
      this.update({ state: 'offline', ...counts })
      return
    }
    if (err instanceof ApiError && err.isAuthProblem) {
      this.update({ state: 'signed-out', ...counts, message: err.message })
      return
    }
    // Die Reise ist weg oder die Rolle reicht nicht mehr. Beides betrifft
    // ausschliesslich den Weg zum Server: hier bleibt alles liegen, wo es liegt,
    // und der Abgleich stellt sich ab, statt im Minutentakt gegen dieselbe Wand
    // zu laufen. Was daraus für die Oberfläche folgt, entscheidet der
    // Aufrufer - er führt zurück zur Reiseliste.
    if (err instanceof ApiError && (err.istUnbekannt || istRollenAbsage(err))) {
      const grund: ZugriffWeg = err.istVerboten ? 'verboten' : 'weg'
      this.update({ state: 'kein-zugriff', ...counts, message: zugriffMeldung(grund) })
      this.stop()
      this.onZugriffWeg(grund)
      return
    }
    const message = err instanceof Error ? err.message : 'Unbekannter Fehler'
    this.update({ state: 'error', ...counts, message })
    console.error('Abgleich fehlgeschlagen', err)
  }

  /**
   * Schiebt alle lokalen Änderungen hoch.
   *
   * @returns ob sich am Bestand etwas geändert hat und wie viele Änderungen
   *   der Server abgewiesen hat.
   */
  private async pushDocs(): Promise<{
    changed: boolean
    abgewiesen: Abweisungen
    gesendet: boolean
  }> {
    let changed = false
    let abgewiesen = KEINE_ABWEISUNG
    let gesendet = false
    // Nur lesende Rolle: es gibt nichts zu senden. Ein Push liefe in ein 403,
    // und das ist keine Auskunft, die der Nutzer bei jedem Durchgang braucht.
    if (!this.darfSchreiben) return { changed, abgewiesen, gesendet }

    for (;;) {
      const dirty = await dirtyDocs(this.tripId)
      if (dirty.length === 0) return { changed, abgewiesen, gesendet }

      gesendet = true
      const batch = dirty.slice(0, PUSH_BATCH)
      const changes: ChangeRequest[] = batch.map((d) => ({
        kind: d.kind,
        id: d.id,
        body: d.deleted ? {} : d.body,
        baseRev: d.rev,
        deleted: d.deleted,
      }))

      const since = await this.cursor()
      const response = await this.api.push(this.tripId, changes, since)

      const updates: LocalDoc[] = []
      for (const result of response.results) {
        const local = batch.find((d) => d.kind === result.kind && d.id === result.id)
        // Zwischen Senden und Antwort kann der Nutzer weitergeschrieben haben.
        // Beide Zweige müssen deshalb den aktuellen Stand nachlesen: der Stand
        // aus dem Batch ist zum Zeitpunkt der Antwort womöglich schon alt, und
        // wer damit entscheidet, verwirft das gerade Getippte.
        const current = await getDoc(this.tripId, result.kind, result.id)

        if (result.status === 'applied') {
          // Geändert heisst: das Dokument bleibt schmutzig und geht im
          // nächsten Lauf mit. Der Vergleich läuft über die Änderungsnummer,
          // nicht über den Zeitstempel: schreibt write() im selben
          // Millisekunden-Tick, sähen zwei verschiedene Fassungen gleich aus.
          const touchedMeanwhile =
            current !== undefined && local !== undefined && current.localSeq !== local.localSeq

          if (touchedMeanwhile) {
            // Der lokale Rumpf ist der jüngere und bleibt stehen, das Dokument
            // geht im nächsten Lauf noch einmal mit. Der Server kennt es jetzt;
            // eine Vorlage ist es damit nicht mehr, sondern der Bestand.
            updates.push({ ...current, rev: result.doc.rev, dirty: 1, seed: undefined })
          } else {
            // Sonst gilt die Fassung des Servers, und zwar samt Rumpf. Hier
            // stand einmal der lokale Rumpf, unter der Annahme, der Server
            // reiche ihn unverändert durch. Das stimmt nicht mehr: er stempelt
            // die Verfasserschaft eines Beitrags hinein (internal/api/posts.go).
            // Wer die eigene Fassung behielte, hielte einen Rumpf, den es beim
            // Server so nicht gibt - unschmutzig, also ohne jeden weiteren
            // Abgleich, und für immer. Beim Import einer fremden Datei ist das
            // kein Randfall, sondern der Regelfall.
            updates.push({ ...toLocal(result.doc), localSeq: current?.localSeq })
          }
        } else if (result.status === 'forbidden') {
          // Der Beitrag gehört jemand anderem, oder die Änderung wollte den
          // alten Tagebuchtext einer Etappe setzen. Beides ist kein Konflikt,
          // der sich beim nächsten Versuch auflöst, sondern eine Absage, die
          // beim nächsten Versuch genauso ausfällt - der Server prüft den Inhalt
          // und nicht die Revision. Also die Fassung des Servers übernehmen und
          // die eigene Änderung fallen lassen; sie schmutzig zu lassen hiesse,
          // sie bis in alle Ewigkeit zu senden.
          //
          // Auch dann, wenn inzwischen weitergetippt wurde: an der Absage
          // ändert das nichts, und was jetzt noch entsteht, wäre derselbe
          // Versuch am selben Dokument.
          updates.push({ ...toLocal(result.doc), localSeq: current?.localSeq })
          abgewiesen =
            result.kind === KIND.diary
              ? { ...abgewiesen, eintraege: abgewiesen.eintraege + 1 }
              : { ...abgewiesen, beitraege: abgewiesen.beitraege + 1 }
          changed = true
        } else {
          updates.push(mergeRemote(current, result.doc))
          changed = true
        }
      }
      await putDocs(this.tripId, updates)

      // Der Push liefert die Gegenrichtung gleich mit.
      if (await this.applyRemote(response.changes, response.cursor)) changed = true

      if (batch.length === dirty.length) return { changed, abgewiesen, gesendet }
    }
  }

  /** Holt die Änderungen der anderen Geräte. */
  private async pullDocs(): Promise<boolean> {
    let changed = false
    for (;;) {
      const since = await this.cursor()
      const response = await this.api.pull(this.tripId, since)
      if (await this.applyRemote(response.changes, response.cursor)) changed = true
      if (!response.hasMore) return changed
    }
  }

  /**
   * Übernimmt fremde Dokumente in den lokalen Bestand.
   *
   * @returns ob sich am Bestand tatsächlich etwas geändert hat. Bewusst nicht
   *   "es kam etwas herein": ein übergangener Foto-Grabstein ändert kein
   *   Dokument, sondern nur eine Notiz an den Bilddaten. Ihn mitzuzählen liesse
   *   die Oberfläche bei jedem Durchgang neu aufbauen, solange ein Upload
   *   aussteht - Arbeit, die niemand sieht, weil sich nichts geändert hat.
   */
  private async applyRemote(remote: ServerDoc[], cursor: number): Promise<boolean> {
    const merged: LocalDoc[] = []
    if (remote.length > 0) {
      for (const doc of remote) {
        // Ein anderes Gerät hat das Foto gelöscht. Was das für die lokalen
        // Bilddaten heisst, hängt daran, ob der Server sie je hatte.
        if (doc.kind === KIND.photo && doc.deleted) {
          const blob = await getBlob(this.tripId, doc.id)
          const action = decidePhotoTombstone(blob)
          if (action === 'ignore') {
            // Der Cursor läuft gleich weiter, der Server liefert diesen
            // Grabstein also nie wieder. Solange der Upload noch aussteht, ist
            // das richtig - er stellt das Foto drüben wieder her. Gibt der
            // Upload später endgültig auf, ist die vermerkte Revision die
            // einzige Spur, über die sich das Gerät noch mit dem Server in
            // Deckung bringen kann.
            if (blob && blob.remoteDeletedRev !== doc.rev) {
              await putBlob(this.tripId, { ...blob, remoteDeletedRev: doc.rev })
            }
            continue
          }
          if (action === 'apply-and-drop-bytes') await deleteBlob(this.tripId, doc.id)
        }

        const local = await getDoc(this.tripId, doc.kind, doc.id)
        merged.push(mergeRemote(local, doc))
      }
      await putDocs(this.tripId, merged)
    }
    await setMeta(this.tripId, CURSOR_KEY, cursor)
    return merged.length > 0
  }

  /**
   * Lädt die Bilddaten hoch, die noch nur auf dem Gerät liegen.
   *
   * Jedes Bild steht für sich: ein Bild, das der Server dauerhaft ablehnt,
   * darf nicht den ganzen Abgleich blockieren. Sonst holte das Gerät nie
   * wieder fremde Änderungen und divergierte still.
   *
   * Aus demselben Grund wird ein vorübergehender Fehler nicht geworfen,
   * sondern zurückgegeben: der Aufrufer holt erst den fremden Bestand und
   * meldet den Fehler danach.
   */
  private async uploadPhotos(): Promise<{ changed: boolean; deleted: boolean; error?: unknown }> {
    // Wie beim Push: ohne Schreibrecht gibt es nichts zu senden.
    if (!this.darfSchreiben) return { changed: false, deleted: false }

    const pending = await pendingBlobs(this.tripId)
    let changed = false
    let deleted = false
    let firstError: unknown

    for (const item of pending) {
      const doc = await getDoc(this.tripId, KIND.photo, item.id)
      if (doc?.deleted) {
        // Vor dem Upload wieder gelöscht: gar nicht erst senden.
        await deleteBlob(this.tripId, item.id)
        continue
      }

      let result
      try {
        result = await this.api.uploadPhoto(this.tripId, item.id, item.stageId, item.blob)
      } catch (err) {
        // Anmeldeprobleme betreffen nicht das Bild, sondern die Sitzung, und
        // eine verlorene Reise betrifft alles auf einmal: beides muss sofort
        // nach oben, statt dem Bild angelastet zu werden. Eine Mengengrenze
        // (too_many_photos) ist dagegen sehr wohl Sache dieses Bildes und geht
        // den gewöhnlichen Weg.
        if (err instanceof ApiError && (err.isAuthProblem || err.istUnbekannt)) throw err
        if (istRollenAbsage(err)) throw err

        const noted = noteUploadFailure(item, err)
        if (noted !== item) await putBlob(this.tripId, noted)

        if (noted.failed === 1) {
          // Aus der Warteschlange gefallen. Kein Fehler nach oben: an den
          // Dokumenten hat sich nichts geändert, und die Statusleiste erfährt
          // es über die Zählung am Ende des Durchlaufs.
          console.error('Foto konnte nicht hochgeladen werden', item.id, err)
          // Wurde während des Wartens ein Grabstein übergangen, weil die
          // Bytes noch ausstanden, wird aus dem Aufschub jetzt eine dauerhafte
          // Abweichung: der Upload kommt nicht mehr, hier stände das Foto also
          // für immer, während Server und alle anderen Geräte es gelöscht
          // haben.
          if (await this.applyMissedTombstone(noted)) changed = true
          continue
        }

        firstError ??= err
        // Ohne Netz braucht der Rest der Warteschlange es gar nicht erst zu
        // versuchen. Ein einzelnes Bild mit Serverfehler hält die anderen
        // dagegen nicht auf.
        if (err instanceof OfflineError) break
        continue
      }

      // Zwischen Anfrage und Antwort kann der Nutzer das Foto gelöscht haben.
      // Dann ist es jetzt beim Server angekommen und muss dort wieder weg,
      // statt lokal wieder aufzutauchen. Nachlesen und Schreiben liegen in
      // einer Transaktion, sonst fällt der Grabstein genau dazwischen.
      //
      // Die Auskunft, ob es dazu kam, wird gebraucht: dann liegt jetzt ein
      // schmutziger Grabstein da, den dieser Durchgang noch nicht gesendet hat -
      // der Push lief vor dem Upload. Der Aufrufer schiebt ihn hinterher.
      const abschluss = await finishPhotoUpload(this.tripId, item, toLocal(result.doc), Date.now())
      if (abschluss.deleted) deleted = true
      changed = true
    }
    return { changed, deleted, error: firstError }
  }

  /**
   * Holt einen übergangenen Grabstein nach, nachdem ein Upload endgültig
   * aufgegeben wurde.
   *
   * Die Bilddaten gehen mit: sie gehören zu einem Foto, das es überall sonst
   * nicht mehr gibt, und ohne sie verschwindet das aufgegebene Bild auch aus
   * der Fehlerzählung der Statusleiste, wo es sonst dauerhaft hängen bliebe.
   *
   * @returns ob sich am Bestand etwas geändert hat.
   */
  private async applyMissedTombstone(blob: LocalBlob): Promise<boolean> {
    const current = await getDoc(this.tripId, KIND.photo, blob.id)
    const doc = tombstoneAfterGiveUp(blob, current, Date.now())
    if (!doc) return false
    await putDocs(this.tripId, [doc])
    await deleteBlob(this.tripId, blob.id)
    return true
  }

  /**
   * Liefert die Bilddaten eines Fotos: aus dem lokalen Speicher, sonst vom
   * Server, wo sie dann auch gleich abgelegt werden.
   */
  async loadPhoto(id: string, stageId: string): Promise<Blob | null> {
    const local = await getBlob(this.tripId, id)
    if (local) return local.blob

    if (!navigator.onLine) return null
    try {
      const blob = await this.api.downloadPhoto(this.tripId, id)

      // Der Download dauert, und in dieser Zeit kann der Bestand gewechselt
      // haben: ein Abmelden leert ihn, ein Import ersetzt ihn, und ein Grabstein
      // von einem anderen Gerät löscht das Foto. Die Bytes dann trotzdem
      // abzulegen, liesse ein Bild ohne Dokument zurück. Das fällt keinem
      // Löschen mehr auf - die gehen über das Dokument - und bliebe liegen,
      // bis die Datenbank als Ganzes geleert wird.
      //
      // Ausgeliefert werden die Bytes trotzdem: der Aufrufer wartet auf ein
      // Bild, das er anzeigen wollte, und was danach damit geschieht, ist die
      // Sache des Aufrufers.
      const doc = await getDoc(this.tripId, KIND.photo, id)
      if (doc && !doc.deleted) {
        await putBlob(this.tripId, {
          id,
          stageId,
          blob,
          uploaded: 1,
          failed: 0,
          createdAt: Date.now(),
        })
      }
      return blob
    } catch (err) {
      if (err instanceof OfflineError) return null
      console.error('Foto konnte nicht geladen werden', id, err)
      return null
    }
  }

  private async cursor(): Promise<number> {
    return (await getMeta<number>(this.tripId, CURSOR_KEY)) ?? 0
  }

  private async counts(): Promise<{ pending: number; failedUploads: number }> {
    const [docs, blobs, failed] = await Promise.all([
      dirtyDocs(this.tripId),
      pendingBlobs(this.tripId),
      failedBlobs(this.tripId),
    ])
    return { pending: docs.length + blobs.length, failedUploads: failed.length }
  }

  private update(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch }
    for (const listener of this.listeners) listener(this.status)
  }

  private readonly handleOnline = () => {
    void this.sync()
  }

  private readonly handleOffline = () => {
    this.update({ state: 'offline' })
  }

  private readonly handleVisibility = () => {
    if (document.visibilityState === 'visible') void this.sync()
  }
}

function toLocal(doc: ServerDoc): LocalDoc {
  return {
    kind: doc.kind,
    id: doc.id,
    body: doc.body,
    rev: doc.rev,
    deleted: doc.deleted,
    dirty: 0,
    updatedAt: doc.updatedAt,
  }
}
